import type { AnalysisType } from "@/components/ai/AnalysisTypeSelector";
import {
  createAudioContext,
  createOfflineAudioContext,
} from "../utils/audio-context";

interface DetectionResult {
  type: AnalysisType;
  confidence: number;
  features: {
    hasVocal: boolean;
    hasInstrument: boolean;
    complexity: number;
  };
}

/**
 * Web Audio API를 사용하여 오디오 파일의 분석 유형을 자동 감지합니다.
 * - Vocal: 음성 주파수 대역 (300Hz - 3400Hz)이 강한 경우
 * - Instrument: 다양한 주파수 대역에 에너지가 분산된 경우
 * - Composition: 복잡한 주파수 구조 + 다층 사운드
 */
export async function detectAnalysisType(
  audioFile: File
): Promise<DetectionResult> {
  return new Promise((resolve, reject) => {
    const audioContext = createAudioContext();
    
    if (!audioContext) {
      reject(new Error("브라우저가 Web Audio API를 지원하지 않습니다"));
      return;
    }
    
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // FFT 분석 설정
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 4096;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        // 오프라인 컨텍스트로 분석
        const offlineContext = createOfflineAudioContext(
          audioBuffer.numberOfChannels,
          audioBuffer.length,
          audioBuffer.sampleRate
        );
        
        if (!offlineContext) {
          reject(new Error("브라우저가 OfflineAudioContext를 지원하지 않습니다"));
          return;
        }

        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineContext.destination);

        const offlineAnalyser = offlineContext.createAnalyser();
        offlineAnalyser.fftSize = 4096;
        source.connect(offlineAnalyser);

        source.start(0);

        // 주파수 데이터 추출
        offlineAnalyser.getByteFrequencyData(dataArray);

        // 주파수 대역별 에너지 계산
        const nyquist = audioBuffer.sampleRate / 2;
        const binWidth = nyquist / bufferLength;

        // 주파수 대역 구분
        const vocalRange = { min: 300, max: 3400 }; // Hz
        const lowRange = { min: 20, max: 300 }; // Bass
        const midRange = { min: 300, max: 2000 }; // Mid
        const highRange = { min: 2000, max: 8000 }; // High
        const veryHighRange = { min: 8000, max: 20000 }; // Very High

        const getEnergyInRange = (min: number, max: number) => {
          const minBin = Math.floor(min / binWidth);
          const maxBin = Math.ceil(max / binWidth);
          let energy = 0;
          for (let i = minBin; i < maxBin && i < dataArray.length; i++) {
            energy += dataArray[i];
          }
          return energy / (maxBin - minBin);
        };

        const vocalEnergy = getEnergyInRange(vocalRange.min, vocalRange.max);
        const lowEnergy = getEnergyInRange(lowRange.min, lowRange.max);
        const midEnergy = getEnergyInRange(midRange.min, midRange.max);
        const highEnergy = getEnergyInRange(highRange.min, highRange.max);
        const veryHighEnergy = getEnergyInRange(
          veryHighRange.min,
          veryHighRange.max
        );

        const totalEnergy =
          vocalEnergy + lowEnergy + midEnergy + highEnergy + veryHighEnergy;

        // 보컬 감지: 중간 주파수 대역이 강하고, 고주파가 적당한 경우
        const vocalRatio = vocalEnergy / totalEnergy;
        const hasVocal = vocalRatio > 0.35;

        // 악기 감지: 다양한 주파수 대역에 고르게 분포
        const energyVariance =
          Math.abs(lowEnergy - midEnergy) +
          Math.abs(midEnergy - highEnergy) +
          Math.abs(highEnergy - veryHighEnergy);
        const avgEnergy =
          (lowEnergy + midEnergy + highEnergy + veryHighEnergy) / 4;
        const normalizedVariance = energyVariance / (avgEnergy * 4);
        const hasInstrument = normalizedVariance < 0.5; // 고른 분포

        // 복잡도: 채널 수 + 주파수 대역 활성화 비율
        const activeFreqBands = [
          lowEnergy > avgEnergy * 0.5,
          midEnergy > avgEnergy * 0.5,
          highEnergy > avgEnergy * 0.5,
          veryHighEnergy > avgEnergy * 0.5,
        ].filter(Boolean).length;

        const complexity =
          (audioBuffer.numberOfChannels > 1 ? 0.5 : 0) +
          (activeFreqBands / 4) * 0.5;

        // 분석 유형 결정
        let type: AnalysisType;
        let confidence: number;

        if (hasVocal && vocalRatio > 0.4) {
          type = "vocal";
          confidence = Math.min(vocalRatio * 2, 0.95);
        } else if (complexity > 0.6 && audioBuffer.numberOfChannels > 1) {
          type = "composition";
          confidence = Math.min(complexity * 1.2, 0.9);
        } else {
          type = "instrument";
          confidence = Math.min(normalizedVariance < 0.3 ? 0.85 : 0.7, 0.9);
        }

        console.log("🔍 분석 유형 감지 결과:", {
          type,
          confidence,
          vocalRatio,
          hasVocal,
          hasInstrument,
          complexity,
          energyDistribution: {
            low: lowEnergy.toFixed(2),
            mid: midEnergy.toFixed(2),
            high: highEnergy.toFixed(2),
            veryHigh: veryHighEnergy.toFixed(2),
          },
        });

        resolve({
          type,
          confidence,
          features: {
            hasVocal,
            hasInstrument,
            complexity,
          },
        });

        audioContext.close();
      } catch (error) {
        console.error("❌ 분석 유형 감지 실패:", error);
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("파일 읽기 실패"));
    };

    reader.readAsArrayBuffer(audioFile);
  });
}
